import useBriefly from '../../hooks/useBriefly';
import { arcPath, arcSegment, GAUGE_GAP } from '../../engine/battlefield/gauge';
import type { MarkerStatus } from '../../engine/battlefield/markerStatus';
import ConditionIcon from '../ConditionIcon';
import clsx from '../../utils/clsx';
import styles from '../../styles/Battlefield.module.css';
import { AMMO_FROM, AMMO_GAP, AMMO_SPAN, CHIP, CHIP_GAP, NUMBERS_MIN_RX } from './markerGeometry';
import type { ScreenXY } from './markerGeometry';

interface MarkerStatusMarksProps {
  /** Whether this viewer may read wound numbers - see useWoundDisplay. */
  showNumbers?: boolean;
  id: string;
  status: MarkerStatus;
  foot: ScreenXY;
  body: ScreenXY;
  rx: number;
  ry: number;
  /** An Object's own ring is its gauge (2026-10-26): no second one round it. */
  noGauge?: boolean;
}

type Ring = { cx: number; cy: number; rx: number; ry: number };

/** How long a reload's refill runs, ms (the beats page: 700). */
const REFILL_MS = 700;

/**
 * A reload (Z1): the magazine has risen and kept its size - never the first time it is seen, nor as it drains, nor a
 * weapon switched (the Track Z review). True for REFILL_MS after.
 */
const reloaded = (was: Rounds, now: Rounds) => now.max === was.max && now.current > was.current;
type Rounds = { current: number; max: number };

/**
 * The magazine's quarter turn. The track is drawn whatever the fraction:
 * an EMPTY wedge says "out of rounds", where no wedge at all says "no gun".
 */
function AmmoWedge({ id, ammo, ring }: { id: string; ammo: NonNullable<MarkerStatus['ammo']>; ring: Ring }) {
  const refilled = useBriefly(ammo, reloaded, REFILL_MS);
  return (
    <g data-testid={`token-ammo-${id}`} data-fraction={ammo.fraction.toFixed(2)}>
      <path className={styles.ammoTrack} d={arcSegment(ring, AMMO_FROM, AMMO_SPAN)} />
      {ammo.fraction > 0 && (
        <path
          // NOT `token-ammo-fill-`: testids are discovered by prefix
          // (`[data-testid^="token-ammo-"]`), so a second family under the
          // same prefix is counted as another marker's gauge and read as an
          // id by every spec that does it.
          data-testid={`token-rounds-${id}`}
          className={clsx(styles.ammoWedge, refilled && styles.ammoRefill)}
          d={arcSegment(ring, AMMO_FROM, AMMO_SPAN * ammo.fraction)}
          stroke={ammo.colour}
          // A reload draws the magazine back round its arc (Z1): the dash runs along a path one unit long.
          pathLength={refilled ? 1 : undefined}
          data-refilled={refilled || undefined}
        />
      )}
    </g>
  );
}

/**
 * The condition chips, worst nearest the body and growing away from it,
 * then a count of any that did not fit. `aria-hidden` because three chips
 * across twenty markers is sixty accessible names inside a scene that is
 * one image - the condition names go in the body's own <title>.
 */
function ConditionChips({ id, status, right, y }: { id: string; status: MarkerStatus; right: number; y: number }) {
  return (
    <>
      {status.chips.map((code, i) => (
        <g
          key={code + i}
          data-testid={`token-chip-${id}-${i}`}
          aria-hidden="true"
          transform={`translate(${right - CHIP - i * (CHIP + CHIP_GAP)},${y - CHIP / 2})`}
        >
          <ConditionIcon code={code} size={CHIP} />
        </g>
      ))}
      {status.overflow > 0 && (
        <text
          data-testid={`token-chip-overflow-${id}`}
          className={styles.chipOverflow}
          x={right - status.chips.length * (CHIP + CHIP_GAP) - 2}
          y={y}
          textAnchor="end"
          dominantBaseline="middle"
        >
          +{status.overflow}
        </text>
      )}
    </>
  );
}

/**
 * The ring, the chips, the ticks and the numbers.
 *
 * WHERE EACH PART GOES, AND WHY THERE. The space around a marker was
 * already almost entirely claimed: the name label sits to the RIGHT of
 * the body, the `⚠` note BELOW the footprint, a move's cost label below
 * that, and a horde's magnitude dead CENTRE on the ground. What was free
 * was the ring itself, the room just outside it, and the space to the left
 * of and above the body.
 *
 * So the marker reads left to right as CHIPS │ BODY │ NAME. Chips go left
 * rather than above because above is where the next marker back is drawn.
 *
 * The ring is drawn OUTSIDE the footprint rather than on it, because the
 * footprint's own stroke is the role colour - and `--bm-health-low` is
 * byte-identical to the red that means "your target". The gap and the
 * dark track keep them apart, and the arc's LENGTH carries the reading.
 */
export default function MarkerStatusMarks({
  id,
  status,
  foot,
  body,
  rx,
  ry,
  showNumbers = true,
  noGauge = false,
}: MarkerStatusMarksProps) {
  const ring = { cx: foot.x, cy: foot.y, rx: rx + GAUGE_GAP, ry: ry + GAUGE_GAP };
  const d = arcPath(ring, status.fraction);
  const bodyR = Math.max(4, rx * 0.75);
  const ammoRing = { cx: foot.x, cy: foot.y, rx: rx + AMMO_GAP, ry: ry + AMMO_GAP };
  const numbersShown = status.numbers && showNumbers && rx >= NUMBERS_MIN_RX;
  return (
    <g
      data-testid={`token-status-${id}`}
      className={styles.tokenStatus}
      data-band={status.band}
      data-fraction={status.fraction.toFixed(2)}
    >
      {!noGauge && <ellipse className={styles.gaugeTrack} cx={ring.cx} cy={ring.cy} rx={ring.rx} ry={ring.ry} />}
      {/* No element at all when there is nothing to sweep - `d=""` is a
          node that paints nothing and still costs one per marker. */}
      {d && !noGauge && (
        <path
          data-testid={`token-gauge-${id}`}
          className={clsx(styles.gauge, status.dead && styles.gaugeDead)}
          d={d}
          stroke={status.colour}
        />
      )}
      {status.ammo && <AmmoWedge id={id} ammo={status.ammo} ring={ammoRing} />}
      <ConditionChips id={id} status={status} right={body.x - bodyR - 4} y={body.y} />
      {numbersShown && (
        <text
          data-testid={`token-vitals-${id}`}
          className={styles.statusNumbers}
          x={body.x}
          y={body.y - bodyR - 6}
          textAnchor="middle"
        >
          {status.numbers}
        </text>
      )}
    </g>
  );
}
